import type { PartialTheme } from '@nivo/theming';
import type { Margin } from '@nivo/core';
import { useSelectByTheme } from '@wix/da-react-context/pkg/ThemeContext';

// Color values from da-ds base-colors-vars.scss and shared-colors-vars.scss
// Dark theme colors
const darkColors = {
  typographyPrimary: '#f2f2f2', // --gray2-1 (--g-typography-primary = --g-theme-primary-opposite)
  typographyTertiary: '#838592', // --gray5 (--g-typography-tertiary = --g-theme-tertiary-opposite)
  divider: '#21272b', // --gray7 (--g-divider = --g-stroke-subtle-on-primary)
  bgPrimary: '#06070d', // --gray9 (--g-bg-primary = --g-theme-primary)
};

// Light theme colors
const lightColors = {
  typographyPrimary: '#06070d', // --gray9 (--g-typography-primary = --g-theme-primary-opposite)
  typographyTertiary: '#838592', // --gray5 (--g-typography-tertiary = --g-theme-tertiary-opposite)
  divider: '#dcdde0', // --gray3 (--g-divider = --g-stroke-subtle-on-primary)
  bgPrimary: '#f2f2f2', // --gray2-1 (--g-bg-primary = --g-theme-primary)
};

// Green theme colors
const greenColors = {
  typographyPrimary: '#213126', // --khaki9 (--g-typography-primary = --g-theme-primary-opposite)
  typographyTertiary: '#617362', // --khaki6 (--g-typography-tertiary = --g-theme-tertiary-opposite)
  divider: '#c0cdbc', // --khaki3-1 (--g-divider = --g-stroke-subtle-on-primary)
  bgPrimary: '#d2decc', // --khaki3 (--g-bg-primary = --g-theme-primary)
};

export const themeDark: PartialTheme = {
  text: {
    fontFamily: 'var(--devious-sans-medium)',
    fontSize: 12,
    fill: darkColors.typographyPrimary,
  },
  axis: {
    ticks: {
      text: {
        fontFamily: 'var(--devious-sans-regular)',
        fill: darkColors.typographyTertiary,
      },
      line: {
        stroke: darkColors.divider,
      },
    },
  },
  grid: {
    line: {
      stroke: darkColors.divider,
    },
  },
  tooltip: {
    container: {
      background: darkColors.bgPrimary,
      color: darkColors.typographyPrimary,
    },
  },
};

export const themeLight: PartialTheme = {
  text: {
    fontFamily: 'var(--devious-sans-medium)',
    fontSize: 12,
    fill: lightColors.typographyPrimary,
  },
  axis: {
    ticks: {
      text: {
        fontFamily: 'var(--devious-sans-regular)',
        fill: lightColors.typographyTertiary,
      },
      line: {
        stroke: lightColors.divider,
      },
    },
  },
  grid: {
    line: {
      stroke: lightColors.divider,
    },
  },
  tooltip: {
    container: {
      background: lightColors.bgPrimary,
      color: lightColors.typographyPrimary,
    },
  },
};

export const themeGreen: PartialTheme = {
  text: {
    fontFamily: 'var(--devious-sans-medium)',
    fontSize: 12,
    fill: greenColors.typographyPrimary,
  },
  axis: {
    ticks: {
      text: {
        fontFamily: 'var(--devious-sans-regular)',
        fill: greenColors.typographyTertiary,
      },
      line: {
        stroke: greenColors.divider,
      },
    },
  },
  grid: {
    line: {
      stroke: greenColors.divider,
    },
  },
  tooltip: {
    container: {
      background: greenColors.bgPrimary,
      color: greenColors.typographyPrimary,
    },
  },
};

export const margin: Margin = { top: 40, right: 25, bottom: 40, left: 40 };

/**
 * Hook to get the theme-aware chart theme based on current theme context
 * @returns The appropriate theme (themeDark, themeLight, or themeGreen)
 */
export const useChartTheme = (): PartialTheme => {
  return useSelectByTheme({
    dark: themeDark,
    light: themeLight,
    green: themeGreen,
  });
};
